/**
 * Role-Based Access Control utilities
 *
 * Abilities are loaded from the GraphQL `me` query into a module-level variable.
 * super_admin grants all abilities (same as backend).
 * In DEV mode, session storage debug overrides take precedence.
 */

import { useSyncExternalStore } from 'react'
import {
  getDebugAbilities,
  subscribeDebugAbilities,
} from '@/lib/debug-abilities'

let abilities: string[] = []
const listeners = new Set<() => void>()

function notifyListeners() {
  listeners.forEach((listener) => listener())
}

export { notifyListeners as notifyAbilityListeners }

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange)

  return () => listeners.delete(onStoreChange)
}

function getSnapshot(): string[] {
  return abilities
}

export function setAbilities(newAbilities: string[]) {
  abilities = newAbilities
  notifyListeners()
}

/** Synchronous getter for current abilities (non-reactive). */
export function getAbilities(): string[] {
  return abilities
}

/**
 * Reactive hook — re-renders when abilities or debug overrides change.
 * Use in components. Use hasAbility() in route guards.
 */
export function useHasAbility(ability: string): boolean {
  const currentAbilities = useSyncExternalStore(subscribe, getSnapshot)
  const debugAbilities = useSyncExternalStore(
    subscribeDebugAbilities,
    getDebugAbilities,
  )

  if (debugAbilities !== null) {
    return (
      debugAbilities.includes(ability) || debugAbilities.includes('super_admin')
    )
  }

  return (
    currentAbilities.includes('super_admin') ||
    currentAbilities.includes(ability)
  )
}

export function isSuperadmin(): boolean {
  return hasAbility('super_admin')
}

/**
 * Synchronous check — reads module-level abilities directly.
 * Use in route guards and non-component code.
 */
export function hasAbility(ability: string): boolean {
  const debugAbilities = getDebugAbilities()

  if (debugAbilities !== null) {
    return (
      debugAbilities.includes(ability) || debugAbilities.includes('super_admin')
    )
  }

  return abilities.includes('super_admin') || abilities.includes(ability)
}
